import { useLazyGetProjectIntegrationsListQuery } from 'src/services';
import { useAuthorizationPolling } from 'src/hooks/useAuthorizationPolling';
import type { ProjectIntegration } from 'src/services/projectsService';

const hasAuthorizedStatus = (integration: ProjectIntegration) =>
  integration.integration_data?.auth_status === 'ok';

// Waits for a project integration's Zendesk OAuth consent after its consent page is opened.
export const useIntegrationAuthorizationPolling = (projectId: number | string) => {
  const [getProjectIntegrationsList] = useLazyGetProjectIntegrationsListQuery();
  const { isCheckingAuthStatus, pollAuthorization } = useAuthorizationPolling();

  // Polls the project integrations until the integration counts as authorized (by default, once
  // its auth_status is "ok"), then passes it to `onAuthorized`.
  const pollZendeskAuthorization = (
    integrationId: number | string,
    onAuthorized: (authorizedIntegration: ProjectIntegration) => void,
    isAuthorized: (integration: ProjectIntegration) => boolean = hasAuthorizedStatus,
  ) =>
    pollAuthorization(async () => {
      const { data: integrations } = await getProjectIntegrationsList({
        project_id: projectId,
        skip_dispatch: true,
      });
      const integration = integrations?.find(({ id }) => id === integrationId);

      return integration && isAuthorized(integration) ? integration : null;
    }, onAuthorized);

  return { isCheckingAuthStatus, pollZendeskAuthorization };
};
